import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Trash2, Check, FlaskConical, Image, Upload, Eye, FileText, Filter, ChevronDown } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { getPetLabResults, addPetLabResult, deletePetLabResult, getLabTypes, downloadFile } from '../services/api';
import { toast } from 'sonner';

const LAB_LABELS = {
  biyokimya: 'Biyokimya', hemogram: 'Hemogram', idrar: 'İdrar Tahlili',
  parazitoloji: 'Parazitoloji', hormon: 'Hormon', seroloji: 'Seroloji',
  mikrobiyoloji: 'Mikrobiyoloji', patoloji: 'Patoloji', diger: 'Diğer',
};

const LAB_COLORS = {
  biyokimya: 'bg-blue-100 text-blue-700', hemogram: 'bg-red-100 text-red-700', idrar: 'bg-yellow-100 text-yellow-700',
  parazitoloji: 'bg-green-100 text-green-700', hormon: 'bg-purple-100 text-purple-700', seroloji: 'bg-pink-100 text-pink-700',
  mikrobiyoloji: 'bg-teal-100 text-teal-700', patoloji: 'bg-orange-100 text-orange-700', diger: 'bg-gray-100 text-gray-700',
};

function Dropdown({ value, options, onChange, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const selected = options.find(o => o.value === value);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} data-testid={testId}
        className={`w-full h-10 px-3 rounded-xl text-sm text-left flex items-center justify-between bg-[#F2F2F7] transition-colors ${selected ? 'text-gray-900' : 'text-[#8E8E93]'}`}>
        <span className="truncate">{selected ? selected.label : placeholder}</span>
        <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-200 max-h-[200px] overflow-y-auto">
          {options.map(o => (
            <button key={o.value} onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-[#F2F2F7] transition-colors flex items-center gap-2 ${
                value === o.value ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-800'
              }`}>
              {o.badge && <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold ${o.badge}`}>{o.label}</span>}
              {!o.badge && o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PetLabModal({ petId, petName, onClose }) {
  const [results, setResults] = useState([]);
  const [text, setText] = useState('');
  const [labType, setLabType] = useState('biyokimya');
  const [filterType, setFilterType] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { loadResults(); }, [petId, filterType]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadResults = async () => {
    try { const res = await getPetLabResults(petId, filterType || null); setResults(res.data); } catch (e) { console.error(e); }
  };

  const handleSubmit = async () => {
    if (!text.trim() && !file) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('lab_type', labType);
      fd.append('text', text.trim());
      if (file) fd.append('file', file);
      await addPetLabResult(petId, fd);
      setText(''); setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      loadResults();
      toast.success('Laboratuvar sonucu eklendi');
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (resultId) => {
    try { await deletePetLabResult(petId, resultId); setDeletingId(null); loadResults(); toast.success('Sonuç silindi'); }
    catch (e) { toast.error('Silinemedi'); }
  };

  const openPreview = async (fileInfo) => {
    try {
      const res = await downloadFile(fileInfo.storage_path);
      const url = URL.createObjectURL(res.data);
      if (fileInfo.content_type === 'application/pdf') {
        window.open(url, '_blank');
      } else {
        setPreviewUrl(url);
      }
    } catch (e) { toast.error('Dosya açılamadı'); }
  };

  const typeOptions = Object.entries(LAB_LABELS).map(([k, v]) => ({ value: k, label: v, badge: LAB_COLORS[k] }));
  const filterOptions = [{ value: '', label: 'Tümü' }, ...typeOptions];

  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-3" data-testid="lab-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white sm:rounded-2xl w-full sm:max-w-md lg:max-w-3xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] shadow-2xl">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
              <FlaskConical size={16} className="text-emerald-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">{petName} - Laboratuvar</h2>
              <p className="text-xs text-[#8E8E93]">{results.length} sonuç</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100" data-testid="close-lab-modal">
            <X size={20} className="text-[#8E8E93]" />
          </button>
        </div>

        {/* Add Lab Result */}
        <div className="px-5 pt-4 pb-2 shrink-0 space-y-2">
          <Dropdown value={labType} options={typeOptions} onChange={setLabType} placeholder="Sonuç türü seçin" testId="lab-type-dropdown" />
          <div className="flex gap-2">
            <Input value={text} onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
              placeholder="Sonuç açıklaması..." className="flex-1 h-10 bg-[#F2F2F7] border-0 rounded-xl text-sm" data-testid="lab-input" />
            <Button onClick={handleSubmit} disabled={saving || (!text.trim() && !file)}
              className="h-10 w-10 p-0 bg-emerald-500 hover:bg-emerald-600 rounded-xl disabled:opacity-40" data-testid="lab-submit">
              <Send size={16} />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => fileRef.current?.click()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors"
              data-testid="lab-file-btn">
              <Upload size={12} /> {file ? file.name : 'Dosya Ekle'}
            </button>
            {file && <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }} className="text-[10px] text-[#FF3B30] hover:underline">Kaldır</button>}
            <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} />
          </div>
        </div>

        {/* Filter */}
        <div className="px-5 pt-2 pb-1 shrink-0">
          <Dropdown value={filterType} options={filterOptions} onChange={setFilterType} placeholder="Filtrele" testId="lab-filter-dropdown" />
        </div>

        {/* Results List */}
        <div className="flex-1 overflow-y-auto px-5 pb-5">
          {results.length > 0 ? (
            <div className="space-y-2 pt-2">
              {results.map(r => (
                <div key={r.id} className="p-3 bg-[#F2F2F7] rounded-xl group" data-testid={`lab-${r.id}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <span className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-semibold mb-1.5 ${LAB_COLORS[r.lab_type] || LAB_COLORS.diger}`}>
                        {LAB_LABELS[r.lab_type] || r.lab_type}
                      </span>
                      {r.text && <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">{r.text}</p>}
                      {r.file && (
                        <button onClick={() => openPreview(r.file)}
                          className="mt-1.5 flex items-center gap-1.5 px-2.5 py-1.5 bg-white rounded-lg text-xs text-emerald-600 hover:bg-emerald-50 transition-colors">
                          {r.file.content_type?.startsWith('image') ? <Image size={12} /> : <FileText size={12} />}
                          <span className="truncate max-w-[180px]">{r.file.original_filename}</span>
                          <Eye size={12} />
                        </button>
                      )}
                      <p className="text-[10px] text-[#8E8E93] mt-1.5">
                        {r.created_by_name && <span className="text-emerald-600">{r.created_by_name} · </span>}
                        {new Date(r.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {deletingId === r.id ? (
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => handleDelete(r.id)} className="p-1.5 rounded-full bg-red-100 hover:bg-red-200"><Check size={14} className="text-[#FF3B30]" /></button>
                        <button onClick={() => setDeletingId(null)} className="p-1.5 rounded-full hover:bg-white"><X size={14} className="text-[#8E8E93]" /></button>
                      </div>
                    ) : (
                      <button onClick={() => setDeletingId(r.id)} className="p-1.5 rounded-full hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        <Trash2 size={14} className="text-[#FF3B30]" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FlaskConical size={36} className="text-[#C7C7CC] mx-auto mb-3" />
              <p className="text-sm text-[#8E8E93]">Henüz laboratuvar sonucu yok</p>
            </div>
          )}
        </div>
      </div>

      {/* Image Preview */}
      {previewUrl && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4" onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
          <img src={previewUrl} alt="Önizleme" className="max-w-full max-h-full rounded-xl object-contain" />
        </div>
      )}
    </div>
  );
}
